import { api } from '@agency/shared';
import { Button, Chip, DataTable, Dialog, Field, Input, PageHeader, Select, Textarea, type DataTableColumn } from '@agency/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorCallout, FilterInput, FilterSelect, IdCell, JsonEditor, LoadMore, Page, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { TEMPLATE_STATE_LABEL, TEMPLATE_STATE_TONE, TEMPLATE_TYPE_SINGULAR } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { usePaged } from '../../lib/useApi.js';

export function TemplatesPage() {
  const session = useSession();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { type?: string; state?: string };
  const [type, setType] = useState<string | undefined>(search.type);
  const [state, setState] = useState<string | undefined>(search.state);
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const input = useMemo(() => ({ limit: 100, ...(type ? { type: type as api.TemplateType } : {}), ...(state ? { state: state as api.TemplateVersionState } : {}), ...(q ? { search: q } : {}) }), [type, state, q]);
  const templates = usePaged('admin.templates.list', { input });
  const canEdit = session.has('template.edit');

  const columns = useMemo<DataTableColumn<api.Template>[]>(
    () => [
      { id: 'id', header: 'Id', cell: ({ row }) => <IdCell value={row.original.id} short={false} tone="brand" />, size: 220 },
      { id: 'name', header: 'Name', cell: ({ row }) => <span className="text-ink">{row.original.name}</span>, size: 260 },
      { id: 'type', header: 'Type', cell: ({ row }) => <span className="font-mono text-xs-plus text-mute">{row.original.type}</span>, size: 160 },
      { id: 'active', header: 'Active', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.activeVersion ? `v${row.original.activeVersion}` : '--'}</span>, size: 70 },
      { id: 'latest', header: 'Latest', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">v{row.original.latestVersion}</span>, size: 70 },
      { id: 'state', header: 'Latest state', cell: ({ row }) => <Chip tone={TEMPLATE_STATE_TONE[row.original.latestState]}>{TEMPLATE_STATE_LABEL[row.original.latestState]}</Chip>, size: 120 },
      { id: 'by', header: 'Created by', cell: ({ row }) => <UserLink userId={row.original.createdByUserId} />, size: 150 },
      { id: 'updated', header: 'Updated', cell: ({ row }) => <Stamp value={row.original.updatedAt} relative />, size: 100 },
    ],
    [],
  );

  return (
    <Page>
      <PageHeader
        eyebrow="Templates and rules"
        eyebrowPath="Templates"
        title="Templates"
        subtitle={templates.loading ? 'Loading templates.' : `${templates.items.length} items loaded${templates.hasMore ? ' (more available below)' : ''} · ${templates.items.filter((t) => t.latestState === 'reviewed').length} awaiting publication · ${templates.items.filter((t) => t.latestState === 'draft').length} drafts`}
        actions={<Button variant="primary" icon={Plus} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New draft</Button>}
      />
      <ErrorCallout error={templates.error} onRetry={() => void templates.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Content items" subtitle={`${templates.items.length} shown${templates.hasMore ? ' · more available' : ''}`} footer={<LoadMore hasMore={templates.hasMore} loading={templates.loadingMore} onLoadMore={() => void templates.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <FilterInput label="Search" value={q} onChange={setQ} placeholder="Name or id" width={240} />
              <FilterSelect label="Type" value={type} onChange={setType} options={api.TEMPLATE_TYPES.map((t) => ({ value: t, label: TEMPLATE_TYPE_SINGULAR[t] }))} width={200} />
              <FilterSelect label="Latest state" value={state} onChange={setState} options={api.TEMPLATE_VERSION_STATES.map((s) => ({ value: s, label: TEMPLATE_STATE_LABEL[s] }))} width={160} />
            </div>
          </div>
          <DataTable<api.Template> aria-label="Templates" columns={columns} rows={templates.items} rowKey={(r) => r.id} height={Math.min(720, Math.max(240, templates.items.length * 34 + 40))} state={templates.loading ? 'loading' : templates.items.length === 0 ? 'empty' : 'complete'} emptyMessage="No templates match these filters." noun="templates" onSelect={(id) => void navigate({ to: '/templates/$templateId', params: { templateId: id } })} onActivate={(id) => void navigate({ to: '/templates/$templateId', params: { templateId: id } })} />
        </TableCard>
      </div>
      <CreateDraftDialog open={createOpen} onOpenChange={setCreateOpen} />
    </Page>
  );
}

function CreateDraftDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const mutation = useMutation();
  const navigate = useNavigate();
  const [type, setType] = useState<api.TemplateType>('remediation_ask');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('{\n  \n}');
  let parsed: unknown = undefined;
  let bodyValid = true;
  try {
    parsed = JSON.parse(body);
  } catch {
    bodyValid = false;
  }
  const valid = name.trim() && summary.trim() && bodyValid && (!id || /^[a-z0-9][a-z0-9._-]{1,79}$/.test(id));
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="New draft" subtitle="Declarative JSON · restricted Markdown" width={640} actions={<><Button variant="tertiary" onClick={() => onOpenChange(false)}>Cancel</Button><Button variant="primary" disabled={!valid} loading={mutation.busy} onClick={async () => { const r = await mutation.run('admin.templates.createDraft', { input: { type, ...(id ? { id: id as api.Template['id'] } : {}), name: name.trim(), ...(description ? { description } : {}), body: parsed, changeSummary: summary.trim() } }, { title: 'Draft created', invalidate: ['admin.templates'] }); if (r) { onOpenChange(false); void navigate({ to: '/templates/$templateId', params: { templateId: r.version.templateId } }); } }}>Create draft</Button></>}>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Type" required><Select value={type} onValueChange={(v) => setType(v as api.TemplateType)} options={api.TEMPLATE_TYPES.map((t) => ({ value: t, label: TEMPLATE_TYPE_SINGULAR[t], hint: t }))} /></Field>
          <Field label="Stable id" hint="OPTIONAL" help="Lowercase; generated from the name when empty."><Input mono value={id} onChange={(e) => setId(e.target.value)} placeholder="ask.mfa-enforcement" /></Field>
        </div>
        <Field label="Name" required><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Description" hint="OPTIONAL"><Input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Change summary" required><Textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} /></Field>
        <JsonEditor value={body} onChange={setBody} rows={10} />
      </div>
    </Dialog>
  );
}
